import { createFileRoute, Link, notFound, redirect, useRouter } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { detectLongWeekends, formatLongWeekendRange } from "@/lib/pseo";
import { getHolidaysForYear } from "@/data/holidays";
import { MONTH_NAMES_ID, MONTH_SLUGS_ID, parseISO } from "@/lib/calendar/dates";
import { withSlash } from "@/lib/url";

/**
 * /harpitnas/$slug routes.
 *
 * - $slug = "YYYY"            → render year overview page
 * - $slug = "YYYY-M-D" (legacy) → 308 redirect to /harpitnas/YYYY/{month}/
 * - anything else             → 404
 */
export const Route = createFileRoute("/harpitnas/$slug/")({
  loader: ({ params }) => {
    const slug = params.slug;
    // Year-only
    if (/^\d{4}$/.test(slug)) {
      const y = parseInt(slug, 10);
      if (y < 1900 || y > 2100) throw notFound();
      return { y };
    }
    // Legacy "YYYY-M-D" → redirect to /harpitnas/YYYY/{month}/
    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(slug);
    if (m) {
      const y = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10);
      if (mo < 1 || mo > 12) throw notFound();
      throw redirect({ to: `/harpitnas/${y}/${MONTH_SLUGS_ID[mo - 1]}/`, code: 308 } as any);
    }
    throw notFound();
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return {};
    const y = loaderData.y;
    const title = `Harpitnas ${y} - Long Weekend & Cuti Strategis Indonesia`;
    const desc = `Daftar lengkap harpitnas dan long weekend ${y} di Indonesia per bulan. Rencanakan cuti agar libur lebih panjang.`;
    const canonical = `https://hari.co.id${withSlash(`/harpitnas/${params.slug}`)}`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
      ],
      links: [{ rel: "canonical", href: canonical }],
    };
  },
  component: HarpitnasYear,
  notFoundComponent: () => (
    <SiteShell>
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold">Halaman tidak ditemukan</h1>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <SiteShell>
        <div className="py-16 text-center">
          <p className="text-sm text-muted-foreground">{error.message}</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground">
            Coba lagi
          </button>
        </div>
      </SiteShell>
    );
  },
});

function HarpitnasYear() {
  const { y } = Route.useLoaderData();
  const holidays = getHolidaysForYear(y);
  const all = detectLongWeekends(y, holidays);
  const harp = all.filter((x) => x.category === "harpitnas");
  const lw = all.filter((x) => x.category === "long-weekend");

  const byMonth: Record<number, typeof all> = {};
  for (const item of all) {
    const a = parseISO(item.startISO)!;
    if (a.y !== y) continue;
    (byMonth[a.m] ||= []).push(item);
  }

  return (
    <SiteShell>
      <div className="mx-auto max-w-4xl py-8">
        <nav className="mb-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">Beranda</Link>{" / "}
          <Link to={withSlash(`/harpitnas`) as never} className="hover:text-primary">Harpitnas</Link>{" / "}
          {y}
        </nav>
        <h1 className="font-display text-4xl tracking-tight md:text-5xl">
          Harpitnas & Long Weekend {y}
        </h1>
        <p className="mt-2 text-muted-foreground">
          {harp.length} harpitnas dan {lw.length} long weekend di tahun {y}.
          Pilih bulan untuk detail per bulan.
        </p>

        <section className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
            const list = byMonth[m] ?? [];
            const harpCount = list.filter((x) => x.category === "harpitnas").length;
            const lwCount = list.filter((x) => x.category === "long-weekend").length;
            return (
              <Link
                key={m}
                to={withSlash(`/harpitnas/${y}/${MONTH_SLUGS_ID[m - 1]}`) as never}
                className="rounded-lg border border-border bg-card p-4 hover:border-primary"
              >
                <div className="font-semibold">{MONTH_NAMES_ID[m - 1]} {y}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {harpCount} harpitnas · {lwCount} long weekend
                </div>
              </Link>
            );
          })}
        </section>

        <section className="mt-10">
          <h2 className="text-2xl font-semibold">Daftar Harpitnas {y}</h2>
          {harp.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Tidak ada harpitnas di {y}.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {harp.map((x) => {
                const a = parseISO(x.startISO)!;
                return (
                  <li key={x.startISO} className="rounded-md border border-border bg-card p-3">
                    <Link
                      to={withSlash(`/harpitnas/${y}/${MONTH_SLUGS_ID[a.m - 1]}`) as never}
                      className="font-medium hover:text-primary"
                    >
                      {formatLongWeekendRange(x)}
                    </Link>
                    <span className="ml-2 text-xs text-muted-foreground">{x.totalDays} hari</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </SiteShell>
  );
}